import { useState, useEffect, useCallback, FocusEvent } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDocumentTypeSelectOption';
import { IKnmReasonChangeTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonChangeTypeSelectOption';
import { IKnmMoveToPlanAttachment } from 'CorePackage/MainModule/interfaces/IKnmMoveToPlanAttachment';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  getCatalogKnmDocumentTypesSelectOptions,
  getCatalogsKnmExclusionReasonsSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { convertFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertFileToBase64';
import { KnmAttachments } from 'KnmPackage/KnmDocumentsModule/components/KnmAttachments';
import { knmPlanExclusionReasonsGet, knmPlanExclusionReasonsReset } from 'KnmPackage/MainModule/actions/knmActions';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { getKnmPlanExclusionReasonsSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { SelectInput, ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { IKnmExclusionReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmExclusionReasonSelectOption';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';

import styles from './KnmRemoveFromPlanModal.module.scss';
import { IRootState } from '../interfaces/IRootState';
import { PmLink } from '../../../KnmErknmPackage/ErknmEventSubjectModule/components/Links/PmLink';
import { IKnmIndex } from '../../../APIPackage/KnmModule/interfaces/IKnmIndex';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';

interface IKnmRemoveFromPlanModalProps {
  isErknm: boolean;
  knmIds: string[];
  onApply: ((reason?: IKnmMoveToPlanAttachment | IKnmExclusionReasonSelectOption) => void) | undefined;
  onClose: () => void;
  visible?: boolean;
  knmDocumentTypesSelectOptions: IKnmDocumentTypeSelectOption[];
  knmExclusionReasonsOptions: IKnmExclusionReasonSelectOption[];
  knmPlanReasonExcludeOptions: IKnmReasonChangeTypeSelectOption[] | undefined;
}

const KnmRemoveFromPlanModalWithoutStore = ({
  isErknm,
  knmIds,
  onApply,
  onClose,
  visible,
  knmDocumentTypesSelectOptions,
  knmExclusionReasonsOptions,
  knmPlanReasonExcludeOptions,
}: IKnmRemoveFromPlanModalProps) => {
  const attachmentType = knmDocumentTypesSelectOptions?.find(el => el.code === 'TYPE_LV');
  const [reason, setReason] = useState<IKnmExclusionReasonSelectOption | ISelectOption | null>(null);
  const [document, setDocument] = useState<File | null>(null);
  const { uploadDocumentSignatures, uploadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [linkedPm, setLinkedPm] = useState<IKnmIndex>();
  const [requisites, setRequisites] = useState<string>();

  const isPreventiveVisit = reason?.value === 'RSN_CHNG_PP_XIX';
  const isProsecutorRequest = reason?.value === 'RSN_CHNG_PP_XXI';
  const isSelectedPmHasLink = !!linkedPm?.knmIdLink;

  const isDisabledButton =
    (isErknm ? !document || !reason : !reason) ||
    (isPreventiveVisit && !linkedPm) ||
    (isProsecutorRequest && !requisites) ||
    (isPreventiveVisit && knmIds?.length > 1) ||
    isSelectedPmHasLink;
  const handleSaveSignedDocument = useCallback(
    async (signedDocument: File, signatureInfo: IKnmAttachment['signatureInfo']) => {
      const attachment = await uploadKnmDocumentAttachment(signedDocument);

      if (onApply && reason && signatureInfo && attachmentType && attachment) {
        onApply({
          document: attachment,
          type: attachmentType,
          reason: reason.value,
          signatureInfo,
          linkedPmId: linkedPm?.id,
          requisites,
        });
        onClose();
      }
    },
    [attachmentType, linkedPm?.id, onApply, onClose, reason, requisites, uploadKnmDocumentAttachment],
  );

  const knmIdsKey = knmIds.join(',');

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (isErknm) {
      const ids = knmIdsKey
        .split(',')
        .map(Number)
        .filter(id => Number.isFinite(id) && id > 0);

      dispatch(knmPlanExclusionReasonsGet(ids));
    } else {
      dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_EXCLUSION_REASONS));
    }
  }, [dispatch, isErknm, knmIdsKey, visible]);

  const handleAfterClose = useCallback(() => {
    setDocument(null);
    setReason(null);
    dispatch(knmPlanExclusionReasonsReset());
  }, [dispatch]);

  const handleChangeReason = useCallback(
    (option: IKnmExclusionReasonSelectOption | IKnmReasonChangeTypeSelectOption) => {
      setReason(option);
    },
    [],
  );

  const handleRemoveDocument = useCallback(() => {
    setDocument(null);
  }, []);

  const handleSetAttachment = useCallback((file: File) => {
    setDocument(file);
  }, []);

  const handleApplyButtonClick = useCallback(async () => {
    if (isErknm && document) {
      const convertedDocument = await convertFileToBase64(document);
      const files = {
        document: convertedDocument,
        signatures: [],
      };
      const res = await uploadDocumentSignatures(files);

      if (res && res.signed) {
        handleSaveSignedDocument(document, res.signatureInfo);
      }
    } else if (!isErknm && onApply && reason) {
      onApply(reason as IKnmExclusionReasonSelectOption);
      onClose();
    }
  }, [document, handleSaveSignedDocument, isErknm, onApply, onClose, reason, uploadDocumentSignatures]);

  const handleTextInputBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    setRequisites(e.target.value);
  }, []);

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title={`Исключение КНМ (${knmIds?.length}) из плана`} sizeText="small" />
      <ModalBody size="content" border="bottom">
        <label className={styles.CommentTextareaLabel}>
          {isErknm ? 'Основание исключения' : 'Введите основание исключения'}
        </label>
        <SelectInput
          name="reasons"
          options={isErknm ? knmPlanReasonExcludeOptions || [] : knmExclusionReasonsOptions}
          onChange={handleChangeReason}
          value={reason}
          isSearchable
          invalid={isPreventiveVisit && knmIds?.length > 1}
          error="Для данного основания должно быть выбрано одно КНМ"
        />

        {isPreventiveVisit && (
          <div className={styles.CommentTextareaLabel}>
            <label className={styles.CommentTextareaLabel}>Ссылка на другой паспорт ПМ</label>
            <PmLink setLinkedPm={setLinkedPm} />
            {isSelectedPmHasLink && (
              <p className="shared-input-error">В паспорте ПМ уже заполнена ссылка на другой паспорт</p>
            )}
            {!linkedPm && <p className="shared-input-error">Поле не может быть пустым</p>}
          </div>
        )}

        {isProsecutorRequest && (
          <div className={styles.CommentTextareaLabel}>
            <label>Реквизиты</label>
            <TextInput placeholder="Введите Реквизиты" onBlur={handleTextInputBlur} />
            {!requisites && <p className="shared-input-error">Поле не может быть пустым</p>}
          </div>
        )}

        {isErknm && (
          <>
            <KnmAttachments
              document={document}
              signs={[]}
              handleRemoveDocument={handleRemoveDocument}
              handleSetAttachment={handleSetAttachment}
              onlyDoc
            />
          </>
        )}
      </ModalBody>
      <ModalActions>
        <Button
          text="Исключить"
          color="primary"
          size="medium"
          type="button"
          disabled={isDisabledButton}
          onClick={handleApplyButtonClick}
        />
        <Button text="Отменить" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState) => ({
  knmDocumentTypesSelectOptions: getCatalogKnmDocumentTypesSelectOptions(state),
  knmExclusionReasonsOptions: getCatalogsKnmExclusionReasonsSelectOptions(state),
  knmPlanReasonExcludeOptions: getKnmPlanExclusionReasonsSelectOptions(state),
});

export const KnmRemoveFromPlanModal = connect(mapStateToProps)(KnmRemoveFromPlanModalWithoutStore);
